import type { FieldType } from '@/types/formSchema';

/** Enum/choice field types: the value is picked, not transcribed — no Heard affordance. */
const HEARD_INELIGIBLE: ReadonlySet<FieldType> = new Set<FieldType>([
  'select',
  'multi_select',
  'radio',
  'checkbox',
  'file',
]);

/**
 * Whether a field shows the "Heard" affordance. Heard is for values the rep says aloud
 * that the agent recovers from the recording later; enum/choice fields are picked
 * directly, so they get no Heard button. Array/object containers are never rendered as
 * a single editable field, so they are ineligible too.
 */
export function isHeardEligible(type: FieldType): boolean {
  if (type === 'array' || type === 'object') return false;
  return !HEARD_INELIGIBLE.has(type);
}

/** Choice types a workflow's table may open to Heard (`ResolvedField.heardAllowed`). */
const HEARD_ALLOWABLE_CHOICES: ReadonlySet<FieldType> = new Set<FieldType>([
  'select',
  'multi_select',
  'radio',
]);

/**
 * Whether a Call Script field shows the "Heard" affordance: every text/number/date field
 * (by type), plus a choice field the workflow allows because the disposition engine does
 * not branch on it (BV-2742, `utils/humanCall/heardRules.ts`). Branching choice fields —
 * coverage status, PA required, … — stay without Heard: left empty until after the call,
 * they would drop their follow-ups from the required set during it.
 */
export function canMarkHeard(field: {
  field: { type: FieldType };
  heardAllowed?: boolean;
}): boolean {
  if (isHeardEligible(field.field.type)) return true;
  return field.heardAllowed === true && HEARD_ALLOWABLE_CHOICES.has(field.field.type);
}
